/**
 * Tâche 1256 · tâches 1278 et 1279 — Le tableau de bord des écarts.
 *
 * Extrait de la page Transcriptions (tâche 1278, Eric 2026-06-29 : « le
 * tableau de bord des écarts n'apparaît encore nulle part ») pour vivre aussi
 * dans son propre écran de menu, sans dupliquer une ligne de code.
 *
 * On y trouve le classement des vidéos par minutes manquantes (1256) et
 * l'injection sur TOUTE la bibliothèque, dans les deux sens (1278) — avec
 * aperçu « N vidéos · M passages » AVANT d'écrire, puis proposition de
 * nettoyage linguistique des seuls passages ajoutés (1279).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../../lib/apiBase";
import BulkGapInjectButtons from "./BulkGapInjectButtons";

export const CoverageGapsDashboard = ({ lang = "fr", onOpenVideo, autoLoad = true }) => {
  // Tâche 1375 (Eric 2026-06-30) — Le tableau des écarts restait français.
  // La langue arrive déjà par la propriété `lang` : on en dérive `tr` plutôt
  // que de relire le contexte, sinon le composant aurait deux sources de
  // vérité et pourrait contredire son appelant.
  const tr = (fr, en) => (lang === "en" ? en : fr);
  const [gapsDash, setGapsDash] = useState(null);
  const [computingGaps, setComputingGaps] = useState(false);
  const loadGapsDash = async (refresh = false) => {
    if (refresh) setComputingGaps(true);
    try {
      const { data } = await axios.get(
        `${API}/api/videos/coverage-gaps-dashboard${refresh ? "?refresh=1" : ""}`,
        { withCredentials: true, timeout: 180000 },
      );
      setGapsDash(data);
      if (refresh) {
        toast.success(
          data.videos_with_gaps
            ? tr(
                `${data.videos_with_gaps} vidéo(s) à corriger, ${data.total_missing_min} min manquantes au total`,
                `${data.videos_with_gaps} video(s) to fix, ${data.total_missing_min} min missing in total`,
              )
            : tr("Aucune vidéo signalée n'a d'écart au-delà des seuils", "No flagged video has a gap beyond the thresholds"),
        );
      }
    } catch (e) {
      if (refresh) toast.error(e?.response?.data?.detail || tr("Calcul impossible", "Could not compute"));
    } finally {
      setComputingGaps(false);
    }
  };

  useEffect(() => { if (autoLoad) loadGapsDash(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [autoLoad]);

  return (
    <section
      className="mb-5 rounded-lg border border-zinc-200 bg-white p-4"
      data-testid="gaps-dashboard"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
            <ChartBar size={16} weight="bold" />
            {lang === "fr" ? "Écarts à corriger en premier" : "Gaps to fix first"}
          </h2>
          <p className="text-xs text-zinc-500 mt-1" data-testid="gaps-dashboard-summary">
            {gapsDash?.computed_at
              ? tr(
                  `${gapsDash.videos_with_gaps} vidéo(s) à corriger sur ${gapsDash.videos_scanned} signalée(s) · ${gapsDash.total_missing_min} min manquantes au total · calculé le ${String(gapsDash.computed_at).slice(0, 16).replace("T", " ")}`,
                  `${gapsDash.videos_with_gaps} video(s) to fix out of ${gapsDash.videos_scanned} flagged · ${gapsDash.total_missing_min} min missing in total · computed on ${String(gapsDash.computed_at).slice(0, 16).replace("T", " ")}`,
                )
              : tr(
                  "Classement des vidéos signalées par l'alerte de couverture, de la plus trouée à la moins. Le calcul relit les transcriptions : lancez-le quand vous en avez besoin.",
                  "Videos flagged by the coverage alert, ranked from the most to the least incomplete. The computation re-reads the transcripts: run it when you need it.",
                )}
          </p>
        </div>
        <button
          onClick={() => loadGapsDash(true)}
          disabled={computingGaps}
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-[#002FA7] text-white hover:bg-[#001f7a] disabled:opacity-50"
          data-testid="gaps-dashboard-compute"
        >
          {computingGaps
            ? (lang === "fr" ? "Calcul en cours…" : "Computing…")
            : gapsDash?.computed_at
              ? (lang === "fr" ? "Recalculer" : "Recompute")
              : (lang === "fr" ? "Calculer le classement" : "Compute ranking")}
        </button>
      </div>

      {/* Tâche 1278 — L'injection sur toute la bibliothèque, un bouton et
          un décompte par sens (composant partagé avec l'Explorateur). */}
      <div className="mt-3 pt-3 border-t border-zinc-100">
        <BulkGapInjectButtons />
      </div>

      {gapsDash?.items?.length > 0 && (
        <ol className="mt-3 space-y-2" data-testid="gaps-dashboard-list">
          {gapsDash.items.map((it, i) => (
            <li
              key={it.video_id}
              className="flex items-center gap-3"
              data-testid={`gaps-dashboard-row-${i}`}
            >
              <span className="font-mono text-xs font-bold text-zinc-400 w-7 shrink-0">
                #{i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                  <span className="text-sm font-medium text-zinc-900 truncate">
                    {it.title}
                  </span>
                  <span
                    className={`self-start sm:self-auto shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                      it.missing_min >= 5
                        ? "bg-red-100 text-red-900"
                        : it.missing_min >= 1
                          ? "bg-amber-100 text-amber-900"
                          : "bg-zinc-100 text-zinc-700"
                    }`}
                    data-testid={`gaps-dashboard-min-${i}`}
                  >
                    {it.missing_min} {tr("min manquantes", "min missing")}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
                  <div
                    className="h-full bg-[#002FA7]/70"
                    style={{
                      width: `${Math.max(
                        3,
                        (it.total_missing_sec /
                          Math.max(1, gapsDash.items[0].total_missing_sec)) * 100,
                      )}%`,
                    }}
                  />
                </div>
                <p className="mt-0.5 text-[11px] text-zinc-500">
                  {tr(
                    `${it.gaps} écart(s) · le plus long ${it.longest_sec} s`,
                    `${it.gaps} gap(s) · longest ${it.longest_sec} s`,
                  )}
                  {it.filled_count > 0
                    ? tr(` · ${it.filled_count} déjà comblé(s) à la main`, ` · ${it.filled_count} already filled by hand`)
                    : ""}
                  {it.module_name ? ` · ${it.module_name}` : ""}
                </p>
              </div>
              <button
                onClick={() => onOpenVideo?.(it.video_id)}
                className="shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-300 hover:bg-zinc-50"
                data-testid={`gaps-dashboard-open-${i}`}
              >
                {lang === "fr" ? "Corriger" : "Fix"} <ArrowRight size={12} />
              </button>
            </li>
          ))}
        </ol>
      )}

      {gapsDash?.computed_at && gapsDash.items.length === 0 && (
        <p className="mt-3 text-xs text-teal-700" data-testid="gaps-dashboard-empty">
          {tr("Aucune vidéo signalée n'a d'écart au-delà des seuils. Rien à corriger.", "No flagged video has a gap beyond the thresholds. Nothing to fix.")}
        </p>
      )}
    </section>
  );
};

export default CoverageGapsDashboard;
